/**
 * Quick Order JavaScript
 * Handles cart operations for the Quick Order page
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 5.1, 5.2, 5.3, 5.4, 5.5, 8.3
 */

class QuickOrder extends HTMLElement {
  constructor() {
    super();
    
    this.selectors = {
      addButton: '.quick-order-add-btn',
      bulkButton: '.quick-order-bulk-btn',
      quantityInput: '.quick-order-qty',
      messageContainer: '.quick-order-message',
      productItem: '.quick-order-item',
      productItem: '.quick-order-item',
      searchInput: '.quick-order-search__input',
      searchClear: '.quick-order-search__clear',
      searchEmpty: '.quick-order-search-empty',
    };
    
    this.debounceTimer = null;
    this.isProcessing = false;
    this.messageCount = 0;
    this.hideTimer = null;
  }

  connectedCallback() {
    this.addButtons = this.querySelectorAll(this.selectors.addButton);
    this.bulkButton = this.querySelector(this.selectors.bulkButton);
    this.messageContainer = this.querySelector(this.selectors.messageContainer);
    
    // Bind event listeners
    this.addButtons.forEach(button => {
      button.addEventListener('click', this.handleSingleAdd.bind(this));
    });
    
    if (this.bulkButton) {
      this.bulkButton.addEventListener('click', this.handleBulkAdd.bind(this));
    }

 // Search/filter
    this.searchInput = this.querySelector(this.selectors.searchInput);
    this.searchClear = this.querySelector(this.selectors.searchClear);
    this.searchEmpty = this.querySelector(this.selectors.searchEmpty);

    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.handleSearch.bind(this));
    }

    if (this.searchClear) {
      this.searchClear.addEventListener('click', this.clearSearch.bind(this));
    }
  }

  /**
   * Handle single product add-to-cart
   * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 8.3
   */
  handleSingleAdd(event) {
    event.preventDefault();
    
    // Debounce to prevent double-clicks - Requirement: 4.3
    if (this.isProcessing) return;
    
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;
    
    // Find the quantity input for this product
    const productRow = button.closest(this.selectors.productItem);
    const quantityInput = productRow.querySelector(this.selectors.quantityInput);
    const quantity = parseInt(quantityInput.value) || 1;
    
    // Validate quantity - Requirement: 4.1
    if (quantity <= 0 || !Number.isInteger(quantity)) {
      this.showMessage('error', 'Please enter a valid quantity');
      return;
    }
    
    this.addToCart(variantId, quantity, productTitle, button);
  }

  /**
   * Handle bulk add-to-cart
   * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
   */
  handleBulkAdd(event) {
    event.preventDefault();
    
    // Debounce to prevent double-clicks
    if (this.isProcessing) return;
    
    const button = event.currentTarget;
    
    // Collect all products with quantity > 0 - Requirement: 5.1
    const items = [];
    const productRows = this.querySelectorAll(this.selectors.productItem);
    
    productRows.forEach(row => {
      const quantityInput = row.querySelector(this.selectors.quantityInput);
      const addButton = row.querySelector(this.selectors.addButton);
      
      if (!addButton || addButton.disabled) return;
      
      const quantity = parseInt(quantityInput.value) || 0;
      
      if (quantity > 0) {
        items.push({
          id: addButton.dataset.variantId,
          quantity: quantity,
          title: addButton.dataset.productTitle
        });
      }
    });
    
    if (items.length === 0) {
      this.showMessage('error', 'Please enter quantities for products you want to add');
      return;
    }
    
    this.addBulkToCart(items, button);
  }

  /**
   * Add single product to cart using Shopify Ajax Cart API
   * Requirements: 4.2, 4.3, 4.4, 4.5, 8.3
   */
  async addToCart(variantId, quantity, productTitle, button) {
    this.isProcessing = true;
    this.toggleLoadingState(button, true);
    
    try {
      // Use Shopify Ajax Cart API - Requirement: 8.3
      const response = await fetch(`${window.FoxTheme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity
          }]
        })
      });
      
      const data = await response.json();
      
      if (data.status || data.description) {
        // Error occurred - Requirement: 4.4
        const errorMessage = data.description || data.message || 'Unable to add product to cart';
        this.showMessage('error', errorMessage);
      } else {
        // Success - Requirement: 4.3
        this.showMessage('success', `${productTitle} added to cart`, 1);
        
        // Update cart count - Requirement: 4.5
        await this.updateCartCount();
      }
    } catch (error) {
      // Network error - Requirement: 4.4
      console.error('Error adding to cart:', error);
      this.showMessage('error', 'Network error. Please try again.');
    } finally {
      this.toggleLoadingState(button, false);
      this.isProcessing = false;
    }
  }

  /**
   * Add multiple products to cart
   * Requirements: 5.2, 5.3, 5.4, 5.5
   */
  async addBulkToCart(items, button) {
    this.isProcessing = true;
    this.toggleLoadingState(button, true);
    
    const successes = [];
    const failures = [];
    
    try {
      // Add all items using Shopify Ajax Cart API - Requirement: 5.2
      const response = await fetch(`${window.FoxTheme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: items.map(item => ({
            id: item.id,
            quantity: item.quantity
          }))
        })
      });
      
      const data = await response.json();
      
      if (data.status || data.description) {
        // Handle errors - Requirement: 5.4
        const errorMessage = data.description || data.message || 'Unable to add some products to cart';
        
        // Try to determine which items failed
        if (data.items && Array.isArray(data.items)) {
          data.items.forEach((item, index) => {
            if (item) {
              successes.push(items[index].title);
            } else {
              failures.push(items[index].title);
            }
          });
        } else {
          // All failed
          failures.push(...items.map(item => item.title));
        }
        
        this.showBulkMessage(successes, failures);
      } else {
        // All succeeded - Requirement: 5.3
        const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
        this.showMessage('success', 'Items added to cart', totalItems);
        
        // Update cart count - Requirement: 5.5
        await this.updateCartCount();
      }
    } catch (error) {
      // Network error - Requirement: 5.4
      console.error('Error adding bulk items to cart:', error);
      this.showMessage('error', 'Network error. Please try again.');
    } finally {
      this.toggleLoadingState(button, false);
      this.isProcessing = false;
    }
  }

  /**
   * Update cart count in header
   * Requirements: 4.5, 5.5
   */
  async updateCartCount() {
    try {
      // Fetch updated cart data
      const response = await fetch(`${window.FoxTheme.routes.cart_url}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      const cart = await response.json();
      
      // Publish cart update event to update cart count
      if (window.FoxTheme && window.FoxTheme.pubsub) {
        window.FoxTheme.pubsub.publish(
          window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          { cart: cart }
        );
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Show success or error message with counter logic
   * Requirements: 4.3, 4.4, 5.3, 5.4
   */
  showMessage(type, message, itemCount = 1) {
    if (!this.messageContainer) return;
    
    // Initialize or increment counter
    this.messageCount = (this.messageCount || 0) + itemCount;
    
    // Determine message text
    let displayMessage;
    if (this.messageCount === 1) {
      displayMessage = message; // "Product Name added to cart"
    } else {
      displayMessage = `${this.messageCount} items added to cart`;
    }
    
    // Show message
    this.messageContainer.hidden = false;
    this.messageContainer.className = `quick-order-message quick-order-message--${type}`;
    this.messageContainer.textContent = displayMessage;
    
    // Dynamic positioning below header
    const header = document.querySelector('header');
    const headerHeight = header ? header.offsetHeight + 20 : 100;
    this.messageContainer.style.top = `${headerHeight}px`;
    
    // Clear existing timer and set new one
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }
    
    this.hideTimer = setTimeout(() => {
      this.messageContainer.hidden = true;
      this.messageCount = 0; // Reset counter
    }, 5000);
  }

  /**
   * Show bulk add message with partial failures
   * Requirement: 5.4
   */
  showBulkMessage(successes, failures) {
    if (!this.messageContainer) return;
    
    let message = '';
    
    if (successes.length > 0) {
      message += `Successfully added: ${successes.join(', ')}. `;
    }
    
    if (failures.length > 0) {
      message += `Failed to add: ${failures.join(', ')}.`;
    }
    
    const type = failures.length > 0 ? 'error' : 'success';
    this.showMessage(type, message);
  }

  /**
   * Toggle loading state on buttons
   * Requirements: 4.3, 5.3
   */
  toggleLoadingState(button, isLoading) {
    if (isLoading) {
      button.disabled = true;
      button.classList.add('btn--loading');
      button.setAttribute('aria-busy', 'true');
    } else {
      button.disabled = false;
      button.classList.remove('btn--loading');
      button.removeAttribute('aria-busy');
    }
  }
   /**
   * Handle search input — filter product rows in real time
   */
  handleSearch(event) {
    const query = event.target.value.trim().toLowerCase();
    const rows = this.querySelectorAll(this.selectors.productItem);
    let visibleCount = 0;

    rows.forEach(row => {
      const title = row.querySelector('.quick-order-item__title');
      const text = title ? title.textContent.trim().toLowerCase() : '';
      const matches = text.includes(query);
      row.hidden = !matches;
      if (matches) visibleCount++;
    });

    if (this.searchClear) {
      this.searchClear.hidden = query.length === 0;
    }

    if (this.searchEmpty) {
      this.searchEmpty.classList.toggle('is-visible', visibleCount === 0 && query.length > 0);
    }
  }

  /**
   * Clear search and restore all rows
   */
  clearSearch() {
    if (this.searchInput) {
      this.searchInput.value = '';
      this.searchInput.dispatchEvent(new Event('input'));
      this.searchInput.focus();
    }
  }
}

// Register custom element
customElements.define('quick-order-list', QuickOrder);

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuickOrder);
} else {
  initQuickOrder();
}

function initQuickOrder() {
  const quickOrderElement = document.querySelector('.quick-order-list');
  if (quickOrderElement && !customElements.get('quick-order-list')) {
    customElements.define('quick-order-list', QuickOrder);
  }
}
